import { useState } from 'react'
import { Target, XCircle } from 'lucide-react'
import type { Microprofile } from '@/consultant-study/types'
import { segments } from '@/consultant-study/segments'
import { getVideoUrl, getVideoPosterUrl } from '@/consultant-study/video-urls'

const SEGMENT_COLORS: Record<string, string> = {
  S1: '#F17922',
  S2: '#2563EB',
  S3: '#DC2626',
  S4: '#6B7280',
  S5: '#8B5CF6',
}

interface ProfileCardProps {
  profile: Microprofile
}

export function ProfileCard({ profile }: ProfileCardProps) {
  const [videoError, setVideoError] = useState(false)

  const segment = segments.find(s => s.id === profile.segmentId)
  const color = SEGMENT_COLORS[profile.segmentId] ?? '#6B7280'

  const initials = profile.nickname
    .split(/\s+/)
    .filter(w => w[0] === w[0].toUpperCase())
    .map(w => w[0])
    .join('')
    .slice(0, 2)

  return (
    <div
      className="border border-gray-200 bg-white overflow-hidden transition-shadow hover:shadow-md"
      style={{ borderTopWidth: 3, borderTopColor: color }}
    >
      {/* Video */}
      <div className="relative w-full aspect-video bg-gray-100">
        {videoError ? (
          <div
            className="flex h-full w-full flex-col items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${color}33 0%, ${color}11 100%)`,
            }}
          >
            <span className="text-4xl font-bold" style={{ color }}>{initials}</span>
            <span className="mt-1 text-xs text-gray-400">Video unavailable</span>
          </div>
        ) : (
          <video
            src={getVideoUrl(profile.id)}
            poster={getVideoPosterUrl(profile.id)}
            controls
            playsInline
            preload="none"
            onError={() => setVideoError(true)}
            className="h-full w-full object-cover"
          />
        )}
      </div>

      {/* Compact info */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span
            className="px-2 py-0.5 text-xs font-bold text-white"
            style={{ backgroundColor: color }}
          >
            {profile.id}
          </span>
          <span className="text-sm font-semibold text-gray-900 truncate">{profile.nickname}</span>
        </div>

        <p className="text-xs italic text-gray-500 leading-relaxed line-clamp-2 mb-3">
          &ldquo;{profile.persona}&rdquo;
        </p>

        <div className="grid grid-cols-2 gap-3">
          <div className="border border-gray-100 p-2">
            <span className="block text-lg font-bold" style={{ color }}>
              {profile.conversionRate}%
            </span>
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">
              Conversion
            </span>
          </div>
          <div className="border border-gray-100 p-2">
            <span className="block text-lg font-bold" style={{ color }}>
              {profile.leadsPerMonth}
            </span>
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">
              Leads / mo
            </span>
          </div>
        </div>

      </div>

      <div className="border-t border-gray-100 px-4 pb-4 space-y-4">
        {/* Strength / Weakness */}
        <div className="space-y-1 pt-3">
            <p className="text-xs">
              <span className="font-medium text-green-700">Strength:</span>{' '}
              <span className="text-gray-600">{profile.keyStrength}</span>
            </p>
            <p className="text-xs">
              <span className="font-medium text-red-700">Weakness:</span>{' '}
              <span className="text-gray-600">{profile.keyWeakness}</span>
            </p>
          </div>

          {/* Lead Matching */}
          <div className="grid grid-cols-2 gap-0 border border-gray-200 overflow-hidden">
            {/* Best Leads */}
            <div className="border-l-3 border-l-green-500 bg-green-50/50 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Target className="h-3.5 w-3.5 text-green-600" />
                <span className="text-xs font-bold uppercase tracking-wider text-green-700">Send These</span>
              </div>
              <ul className="space-y-1.5">
                {profile.bestLeads.map(lead => (
                  <li key={lead} className="flex items-start gap-1.5">
                    <span className="mt-1.5 w-1 h-1 bg-green-500 shrink-0" />
                    <span className="text-[11px] leading-tight text-green-800">{lead}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Avoid Leads */}
            <div className="border-l border-l-gray-200 bg-red-50/50 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <XCircle className="h-3.5 w-3.5 text-red-500" />
                <span className="text-xs font-bold uppercase tracking-wider text-red-700">Never Send</span>
              </div>
              <ul className="space-y-1.5">
                {profile.avoidLeads.map(lead => (
                  <li key={lead} className="flex items-start gap-1.5">
                    <span className="mt-1.5 w-1 h-1 bg-red-400 shrink-0" />
                    <span className="text-[11px] leading-tight text-red-800">{lead}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Demographics strip */}
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <span className="bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
              {profile.demographics.experience}yr exp
            </span>
            <span className="bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
              {profile.demographics.genderFemale}% female
            </span>
            <span className="bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500 truncate max-w-[200px]">
              {profile.demographics.education}
            </span>
          </div>
        </div>
      </div>
  )
}
